import { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './FJImageCropModal.module.less';
import FJModalClose from '../FJModalClose/FJModalClose';
import FJButton, { FJButtonType } from '../../../common/components/FJButton/FJButton';
import FJImageCropUtil from '../../../util/FJImageCropUtil';
import FJLocalStore from '../../../store/FJLocalStore';
import classNames from 'classnames';
import { useMemo } from 'react';

const _getCropHandles = screenCropRect => {
    const handleSize = 12;
    const verticalWidth = 6;
    const horizontalWidth = 16;
    return FJImageCropUtil.getCropHandles(screenCropRect, handleSize, verticalWidth, horizontalWidth);
};

const _getIsOnHandles = (x, y, screenCropRect) => {
    const handleSize = 12;
    const verticalWidth = 6;
    const horizontalWidth = 16;
    return FJImageCropUtil.getIsOnHandles(x, y, {
        screenCropRect,
        handleSize,
        verticalWidth,
        horizontalWidth,
    });
};

const FJImageCropModal = ({
    imageUrl,
    onConfirm,
    onCancel,
    minHeight,
    minWidth,
    maxHeight,
    maxWidth,
    hasCropRatio,
    initCropRatio,
    initCropRect,
}) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [scaleInfo, setScaleInfo] = useState(null);
    const [cropRect, setCropRect] = useState({ x: 0, y: 0, width: 200, height: 200 });
    const [originalImage, setOriginalImage] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [resizeHandle, setResizeHandle] = useState(null);
    const [isCropping, setIsCropping] = useState(false);
    const [cropRatio, setCropRatio] = useState(undefined);
    const getInitConstrainedRect = useCallback(
        (ratio, scaleInfoNew) => {
            const { originalWidth, originalHeight } = scaleInfoNew || scaleInfo;
            const originRatio = originalWidth / originalHeight;
            let constrainedRect = {
                x: 0,
                y: 0,
                width: originalWidth,
                height: originalHeight,
            };
            if (ratio) {
                if (originRatio > ratio) {
                    constrainedRect.width = constrainedRect.height * ratio;
                } else {
                    constrainedRect.height = constrainedRect.width / ratio;
                }
            }
            constrainedRect.x = (originalWidth - constrainedRect.width) / 2;
            constrainedRect.y = (originalHeight - constrainedRect.height) / 2;
            return constrainedRect;
        },
        [scaleInfo],
    );
    const menuItems = [
        {
            text: FJLocalStore._('horizontal'),
            value: 16 / 9,
        },
        {
            text: FJLocalStore._('vertical'),
            value: 9 / 16,
        },
    ];
    const subTabIndex = useMemo(() => {
        const idx = menuItems.findIndex(item => item.value === cropRatio);
        return idx !== -1 ? idx : 0;
    }, [cropRatio]);
    useEffect(() => {
        const loadImage = async () => {
            if (!imageUrl) return;

            try {
                setIsLoading(true);

                // 加载原始图片
                const img = new Image();
                img.crossOrigin = 'anonymous';

                img.onload = () => {
                    // 保存原始图片
                    setOriginalImage(img);

                    // 计算缩放信息，使用cover模式并添加20像素边距
                    const container = containerRef.current;
                    const padding = 20;
                    const availableWidth = container.clientWidth - padding * 2;
                    const availableHeight = container.clientHeight - padding * 2;
                    const scaleInfo = FJImageCropUtil.calculateImageScale(
                        img.width,
                        img.height,
                        availableWidth,
                        availableHeight,
                        'contain',
                    );

                    setScaleInfo(scaleInfo);

                    // 设置初始裁剪区域为图片大小的90%，居中显示
                    const cropSize = 1; // 90%大小
                    const initialCropRect = {
                        x: (scaleInfo.originalWidth * (1 - cropSize)) / 2,
                        y: (scaleInfo.originalHeight * (1 - cropSize)) / 2,
                        width: scaleInfo.originalWidth * cropSize,
                        height: scaleInfo.originalHeight * cropSize,
                    };

                    // 应用尺寸限制
                    let constrainedRect = FJImageCropUtil.constrainCropRect(
                        initialCropRect,
                        {
                            originalWidth: scaleInfo.originalWidth,
                            originalHeight: scaleInfo.originalHeight,
                        },
                        {
                            minWidth,
                            minHeight,
                            maxWidth,
                            maxHeight,
                        },
                    );
                    if (hasCropRatio) {
                        const ratio = initCropRatio || menuItems[0].value;
                        constrainedRect = initCropRect || getInitConstrainedRect(ratio, scaleInfo);
                        setCropRatio(ratio);
                    }
                    setCropRect(constrainedRect);
                    setIsLoading(false);
                };

                img.onerror = () => {
                    console.error('Failed to load image');
                    setIsLoading(false);
                };

                img.src = imageUrl;
            } catch (error) {
                console.error('Failed to load image:', error);
                setIsLoading(false);
            }
        };

        loadImage();
    }, [imageUrl, minHeight, minWidth, maxHeight, maxWidth, hasCropRatio, initCropRatio, initCropRect]);

    // 绘制Canvas
    const drawCanvas = useCallback(() => {
        if (!scaleInfo || !canvasRef.current || !originalImage || !containerRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const container = containerRef.current;
        const padding = 20;

        // 设置Canvas尺寸为容器尺寸
        canvas.width = container.clientWidth;
        canvas.height = container.clientHeight;

        // 设置Canvas底色
        ctx.fillStyle = '#ececf0';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // 绘制图片（居中cover显示，带边距）
        const imageX = padding + scaleInfo.offsetX;
        const imageY = padding + scaleInfo.offsetY;
        ctx.drawImage(originalImage, imageX, imageY, scaleInfo.displayWidth, scaleInfo.displayHeight);

        // 绘制遮罩
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(imageX, imageY, scaleInfo.displayWidth, scaleInfo.displayHeight);

        // 绘制裁剪区域（屏幕坐标，考虑图片偏移）
        const screenCropRect = {
            x: imageX + cropRect.x * scaleInfo.scale,
            y: imageY + cropRect.y * scaleInfo.scale,
            width: cropRect.width * scaleInfo.scale,
            height: cropRect.height * scaleInfo.scale,
        };

        // 清除裁剪区域
        ctx.clearRect(screenCropRect.x, screenCropRect.y, screenCropRect.width, screenCropRect.height);

        // 重新绘制裁剪区域内的图片
        ctx.drawImage(
            originalImage,
            cropRect.x,
            cropRect.y,
            cropRect.width,
            cropRect.height,
            screenCropRect.x,
            screenCropRect.y,
            screenCropRect.width,
            screenCropRect.height,
        );

        // 绘制九宫格辅助线
        FJImageCropUtil.drawRuleOfThirdsGrid(ctx, screenCropRect, {
            lineWidth: 1,
            lineColor: 'rgba(255,255,255,0.35)',
        });

        // 绘制裁剪框边框
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.strokeRect(screenCropRect.x, screenCropRect.y, screenCropRect.width, screenCropRect.height);

        // 绘制调整手柄
        ctx.fillStyle = '#ffffff';

        // 设置阴影效果
        ctx.shadowBlur = 5;
        ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;

        // 获取所有手柄
        let handles = _getCropHandles(screenCropRect);

        // 固定大小比例时，只显示四个角的手柄
        if (hasCropRatio) {
            handles = handles.slice(0, 4);
        }
        handles.forEach(handle => {
            if (['nw', 'ne', 'sw', 'se'].includes(handle.type)) {
                // 四个角的手柄：圆形
                ctx.beginPath();
                // 绘制圆形需要偏离到圆心位置
                ctx.arc(handle.x + handle.width / 2, handle.y + handle.height / 2, handle.width / 2, 0, 2 * Math.PI);
                ctx.fill();
            } else {
                // 绘制圆角矩形
                const radius = 3;
                ctx.beginPath();
                if (ctx.roundRect) {
                    ctx.roundRect(handle.x, handle.y, handle.width, handle.height, radius);
                }
                ctx.fill();
            }
        });

        // 清除阴影设置
        ctx.shadowBlur = 0;
        ctx.shadowColor = 'transparent';
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
    }, [originalImage, scaleInfo, cropRect, hasCropRatio]);

    // 更新Canvas绘制
    useEffect(() => {
        if (!isLoading) {
            drawCanvas();
        }
    }, [isLoading, drawCanvas]);

    // 处理鼠标按下
    const handleMouseDown = useCallback(
        e => {
            if (!scaleInfo || !containerRef.current) return;

            const rect = canvasRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            const padding = 20;
            const imageX = padding + scaleInfo.offsetX;
            const imageY = padding + scaleInfo.offsetY;

            const screenCropRect = {
                x: imageX + cropRect.x * scaleInfo.scale,
                y: imageY + cropRect.y * scaleInfo.scale,
                width: cropRect.width * scaleInfo.scale,
                height: cropRect.height * scaleInfo.scale,
            };

            // 检查是否点击在调整手柄上
            const clickedHandle = _getIsOnHandles(x, y, screenCropRect);

            if (clickedHandle) {
                setIsResizing(true);
                setResizeHandle(clickedHandle.type);
            } else if (
                x >= screenCropRect.x &&
                x <= screenCropRect.x + screenCropRect.width &&
                y >= screenCropRect.y &&
                y <= screenCropRect.y + screenCropRect.height
            ) {
                setIsDragging(true);
            }

            setDragStart({ x, y });
        },
        [scaleInfo, cropRect],
    );

    // 处理鼠标移动
    const handleMouseMove = useCallback(
        e => {
            if (!scaleInfo || !canvasRef.current || !containerRef.current) return;

            const rect = canvasRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            const padding = 20;
            const imageX = padding + scaleInfo.offsetX;
            const imageY = padding + scaleInfo.offsetY;

            // 检测是否在四个角落resize
            const screenCropRect = {
                x: imageX + cropRect.x * scaleInfo.scale,
                y: imageY + cropRect.y * scaleInfo.scale,
                width: cropRect.width * scaleInfo.scale,
                height: cropRect.height * scaleInfo.scale,
            };

            const hoveredHandle = _getIsOnHandles(x, y, screenCropRect);

            // 设置 cursor 样式
            if (isDragging) {
                canvasRef.current.style.cursor = 'move';
            } else if (isResizing) {
                switch (resizeHandle) {
                    case 'nw':
                        canvasRef.current.style.cursor = 'nwse-resize';
                        break;
                    case 'ne':
                        canvasRef.current.style.cursor = 'nesw-resize';
                        break;
                    case 'sw':
                        canvasRef.current.style.cursor = 'nesw-resize';
                        break;
                    case 'se':
                        canvasRef.current.style.cursor = 'nwse-resize';
                        break;
                    case 'n':
                    case 's':
                        canvasRef.current.style.cursor = 'ns-resize';
                        break;
                    case 'w':
                    case 'e':
                        canvasRef.current.style.cursor = 'ew-resize';
                        break;
                    default:
                        canvasRef.current.style.cursor = 'move';
                }
            } else if (hoveredHandle) {
                switch (hoveredHandle.type) {
                    case 'nw':
                        canvasRef.current.style.cursor = 'nwse-resize';
                        break;
                    case 'ne':
                        canvasRef.current.style.cursor = 'nesw-resize';
                        break;
                    case 'sw':
                        canvasRef.current.style.cursor = 'nesw-resize';
                        break;
                    case 'se':
                        canvasRef.current.style.cursor = 'nwse-resize';
                        break;
                    case 'n':
                    case 's':
                        canvasRef.current.style.cursor = 'ns-resize';
                        break;
                    case 'w':
                    case 'e':
                        canvasRef.current.style.cursor = 'ew-resize';
                        break;
                    default:
                        canvasRef.current.style.cursor = 'move';
                }
            } else {
                // 检查是否在裁剪区域内
                if (
                    x >= screenCropRect.x &&
                    x <= screenCropRect.x + screenCropRect.width &&
                    y >= screenCropRect.y &&
                    y <= screenCropRect.y + screenCropRect.height
                ) {
                    canvasRef.current.style.cursor = 'move';
                } else {
                    canvasRef.current.style.cursor = 'default';
                }
            }

            if (!isDragging && !isResizing) return;

            // 调整裁剪框
            const deltaX = x - dragStart.x;
            const deltaY = y - dragStart.y;

            if (isDragging) {
                // 拖拽整个裁剪区域
                const newX = cropRect.x + deltaX / scaleInfo.scale;
                const newY = cropRect.y + deltaY / scaleInfo.scale;

                const constrainedRect = FJImageCropUtil.constrainCropRect(
                    { ...cropRect, x: newX, y: newY },
                    { originalWidth: scaleInfo.originalWidth, originalHeight: scaleInfo.originalHeight },
                    { minWidth, minHeight, maxWidth, maxHeight },
                );

                setCropRect(constrainedRect);
            } else if (isResizing) {
                // 调整裁剪区域大小
                const newCropRect = FJImageCropUtil.resizeCropRect(
                    cropRect,
                    resizeHandle,
                    deltaX,
                    deltaY,
                    scaleInfo.scale,
                    { minWidth, minHeight, maxWidth, maxHeight, ratio: cropRatio },
                    { width: scaleInfo.originalWidth, height: scaleInfo.originalHeight },
                );

                const constrainedRect = FJImageCropUtil.constrainCropRect(
                    newCropRect,
                    {
                        originalWidth: scaleInfo.originalWidth,
                        originalHeight: scaleInfo.originalHeight,
                    },
                    {
                        minWidth,
                        minHeight,
                        maxWidth,
                        maxHeight,
                        ratio: cropRatio,
                    },
                );

                setCropRect(constrainedRect);
            }

            setDragStart({ x, y });
        },
        [
            scaleInfo,
            cropRect,
            isDragging,
            isResizing,
            resizeHandle,
            dragStart,
            minHeight,
            minWidth,
            maxHeight,
            maxWidth,
            cropRatio,
        ],
    );

    // 处理鼠标抬起
    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
        setIsResizing(false);
        setResizeHandle(null);
    }, []);

    // 清空画布
    const clearCanvas = useCallback(() => {
        if (canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    }, []);

    // 确认裁剪
    const handleConfirm = useCallback(async () => {
        try {
            setIsCropping(true);
            setIsCropping;
            if (!originalImage) return;

            // 创建临时Canvas用于裁剪
            const tempCanvas = document.createElement('canvas');
            const tempCtx = tempCanvas.getContext('2d');

            // 设置Canvas尺寸为原始图片尺寸
            tempCanvas.width = originalImage.width;
            tempCanvas.height = originalImage.height;

            // 绘制原始图片
            tempCtx.drawImage(originalImage, 0, 0);

            // 使用原始图片尺寸进行裁剪
            const file = await FJImageCropUtil.exportCropImage(tempCanvas, cropRect, {
                maxSize: 1024 * 1024 * 2,
                cropRatio,
            });
            setIsCropping(false);
            clearCanvas();
            onConfirm(file);
        } catch (error) {
            setIsCropping(false);
            console.error('Failed to crop image:', error);
        }
    }, [originalImage, cropRect, onConfirm, clearCanvas, cropRatio]);

    // 取消裁剪
    const handleCancel = useCallback(() => {
        clearCanvas();
        onCancel();
    }, [clearCanvas, onCancel]);

    // 重置裁剪区域
    const handleReset = useCallback(() => {
        // 设置初始裁剪区域为整个图片大小
        const initialCropRect = getInitConstrainedRect(cropRatio);

        // 应用尺寸限制
        const constrainedRect = FJImageCropUtil.constrainCropRect(
            initialCropRect,
            {
                originalWidth: scaleInfo.originalWidth,
                originalHeight: scaleInfo.originalHeight,
            },
            {
                minWidth,
                minHeight,
                maxWidth,
                maxHeight,
            },
        );

        setCropRect(constrainedRect);
    }, [maxHeight, maxWidth, minHeight, minWidth, scaleInfo, cropRatio, getInitConstrainedRect]);

    const _onChangeSubTab = index => {
        const ratio = menuItems[index].value;
        const constrainedRect = getInitConstrainedRect(ratio);
        setCropRatio(ratio);
        setCropRect(constrainedRect);
    };
    return (
        <div className={styles.crop_modal}>
            <FJModalClose onClick={handleCancel} />
            <div className={styles.modal_content}>
                {hasCropRatio && (
                    <div className={styles.header_tab}>
                        {/* todo 转换成公共组件 */}
                        <div className={styles.tab_box}>
                            {menuItems.map((item, index) => {
                                return (
                                    <div
                                        key={item.value}
                                        className={classNames(
                                            styles.tab_item,
                                            index === subTabIndex && styles.active_tab,
                                        )}
                                        onClick={() => _onChangeSubTab(index)}
                                    >
                                        {FJLocalStore._(item.text)}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                <div className={styles.canvas_container} ref={containerRef}>
                    <canvas
                        ref={ref => {
                            canvasRef.current = ref;
                        }}
                        className={styles.canvas}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                    />
                </div>

                <div className={styles.footer}>
                    <div className={styles.header}>
                        <h3 className={styles.title}>{FJLocalStore._('headshot-crop-title')}</h3>
                    </div>
                    <FJButton
                        text={FJLocalStore._('reset')}
                        type={FJButtonType.OUTLINE}
                        onClick={handleReset}
                        className={styles.cancel_button}
                    />
                    <FJButton
                        text={FJLocalStore._('confirm')}
                        type={FJButtonType.GRADIENT}
                        onClick={handleConfirm}
                        disabled={isLoading}
                        className={styles.confirm_button}
                        isLoading={isCropping}
                    />
                </div>
            </div>
        </div>
    );
};

FJImageCropModal.propTypes = {
    imageUrl: PropTypes.string,
    onConfirm: PropTypes.func,
    onCancel: PropTypes.func,
    minHeight: PropTypes.number,
    minWidth: PropTypes.number,
    maxHeight: PropTypes.number,
    maxWidth: PropTypes.number,
    hasCropRatio: PropTypes.bool,
    initCropRatio: PropTypes.number,
    initCropRect: PropTypes.object,
};

export default FJImageCropModal;
